import type { Ref } from 'vue';

import { computed } from 'vue';
import { useRoute } from 'vue-router';

import * as routeQueryHelpers from '@/helpers/routeQuery';
import { defCurPage, defPageSize } from '@/constants/routeQuery';

export interface DefRouteQuery {
  curPage?: number;
  pageSize?: number;
  status?: string[];
  search?: string;
  isRfc?: boolean;
  isUnread?: boolean;
}
export interface RouteQueryComposable {
  curPage: Readonly<Ref<number>>;
  pageSize: Readonly<Ref<number>>;
  status: Readonly<Ref<DefRouteQuery['status']>>;
  search: Readonly<Ref<DefRouteQuery['search']>>;
  isRfc: Readonly<Ref<DefRouteQuery['isRfc']>>;
  isUnread: Readonly<Ref<DefRouteQuery['isUnread']>>;
}

export default function useTicketsRouteQuery(
  defRouteQuery: DefRouteQuery,
): RouteQueryComposable {
  if (defRouteQuery.curPage == null) {
    defRouteQuery.curPage = defCurPage;
  }
  if (defRouteQuery.pageSize == null) {
    defRouteQuery.pageSize = defPageSize;
  }
  const _defCurPage = defRouteQuery.curPage;
  const _defPageSize = defRouteQuery.pageSize;
  const _defStatus = defRouteQuery.status;
  const _defSearch = defRouteQuery.search;
  const _defIsRfc = defRouteQuery.isRfc;
  const _defIsUnread = defRouteQuery.isUnread;

  const route = useRoute();

  const curPage = computed<number>(() => {
    return (
      routeQueryHelpers.getQueryNumberValue(route.query.page, _defCurPage) ??
      _defCurPage
    );
  });

  const pageSize = computed<number>(() => {
    return (
      routeQueryHelpers.getQueryNumberValue(
        route.query.page_size,
        _defPageSize,
      ) ?? _defPageSize
    );
  });

  const status = computed<DefRouteQuery['status']>(() => {
    return routeQueryHelpers.getQueryStringListValue(
      route.query.status,
      _defStatus,
    );
  });

  const search = computed<DefRouteQuery['search']>(() => {
    return routeQueryHelpers.getQueryStringValue(
      route.query.search,
      _defSearch,
    );
  });

  const isRfc = computed<DefRouteQuery['isRfc']>(() => {
    return routeQueryHelpers.getQueryBooleanValue(
      route.query.is_rfc,
      _defIsRfc,
    );
  });

  const isUnread = computed<DefRouteQuery['isUnread']>(() => {
    return routeQueryHelpers.getQueryBooleanValue(
      route.query.is_unread,
      _defIsUnread,
    );
  });

  return {
    curPage,
    pageSize,
    status,
    search,
    isRfc,
    isUnread,
  };
}
